<template>
  <div class="mb-10">
    <div v-if="loading" class="text-center py-16">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <template v-else-if="detail">
      <!-- header -->
      <div class="d-flex align-center">
        <v-btn variant="text" size="small" @click="backToList">
          <v-icon start>mdi-chevron-left</v-icon>
          一覧へ戻る
        </v-btn>
      </div>
      <div class="d-flex align-center mt-1">
        <div>
          <h2>{{ planLabel }}</h2>
          <div class="text-caption text-grey-darken-1 mt-1">
            <span v-if="detail.plan.templateName">レイアウト: {{ detail.plan.templateName }}</span>
            <span class="ml-3">第{{ detail.plan.version }}版</span>
            <span v-if="detail.plan.deadlineDate" class="ml-3">
              提出期限: {{ formatDate(detail.plan.deadlineDate) }}
              <span v-if="isDeadlineOverdue" class="deadline-overdue ml-1">期限超過</span>
            </span>
            <span class="ml-3">作成者: {{ detail.plan.authorName }}</span>
          </div>
        </div>
        <v-spacer />
        <div
          class="status-badge"
          :style="{
            backgroundColor: statusStyles[detail.plan.status].background,
            color: statusStyles[detail.plan.status].color,
          }"
        >
          {{ statusStyles[detail.plan.status].label }}
        </div>
      </div>

      <!--
        承認の進行状況。誰の承認待ちなのか・いつ承認されたのかが
        ステータスバッジだけでは分からないため、提出から最終承認まで並べる。
      -->
      <div v-if="workflowSteps.length > 0" class="tp-workflow mt-3">
        <div
          v-for="(step, index) in workflowSteps"
          :key="index"
          class="tp-workflow__step"
          :class="`tp-workflow__step--${step.state}`"
        >
          <v-icon size="15">{{ workflowStepIcons[step.state] }}</v-icon>
          <span class="tp-workflow__label">{{ step.label }}</span>
          <span v-if="step.name" class="tp-workflow__name">{{ step.name }}</span>
          <span v-if="step.at" class="tp-workflow__at">{{ formatDateTime(step.at) }}</span>
          <span v-else-if="step.state === 'current'" class="tp-workflow__at">承認待ち</span>
        </div>
      </div>

      <!-- 差戻しバナー -->
      <v-alert
        v-if="detail.plan.status === 'returned' && detail.workflow.returnedReason"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        <div class="font-weight-bold">
          差戻しされました（{{ formatDateTime(detail.workflow.returnedAt) }}）
        </div>
        <div class="tp-pre">{{ detail.workflow.returnedReason }}</div>
      </v-alert>

      <!-- 承認者レビューバー -->
      <v-card
        v-if="detail.permissions.canApprove || detail.permissions.canReturn"
        variant="tonal"
        color="primary"
        class="mt-3 pa-3 d-flex align-center"
      >
        <div>
          <div class="font-weight-bold">承認をお願いします</div>
          <div class="text-caption">
            {{ approverSubText }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          v-if="detail.permissions.canReturn"
          class="mr-2"
          color="error"
          variant="flat"
          :disabled="processing"
          @click="returnDialog = true"
        >
          差戻し
        </v-btn>
        <v-btn
          v-if="detail.permissions.canApprove"
          color="primary"
          variant="flat"
          :loading="approving"
          :disabled="processing"
          @click="approve"
        >
          承認する
        </v-btn>
      </v-card>

      <!-- 編集ロック表示 -->
      <v-alert
        v-else-if="!detail.permissions.canEdit"
        type="info"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        {{ lockedReason }}
      </v-alert>

      <!-- toolbar -->
      <v-card variant="flat" color="grey-lighten-4" class="mt-3 pa-2 d-flex align-center flex-wrap ga-2">
        <div
          class="text-caption text-grey-darken-1 mr-2 d-flex align-center"
          style="min-width: 150px"
        >
          <template v-if="saving">
            <v-icon size="14">mdi-cloud-upload-outline</v-icon>
            保存中...
          </template>
          <!-- 通信の失敗より先に出す。直せば保存されるので、再試行より修正が先 -->
          <template v-else-if="invalidCellCount > 0">
            <span class="text-error">
              <v-icon size="14">mdi-alert-circle-outline</v-icon>
              {{ invalidCellCount }}件が保存できません
            </span>
          </template>
          <template v-else-if="saveFailed">
            <span class="text-error">
              <v-icon size="14">mdi-alert-circle-outline</v-icon>
              保存に失敗しました
            </span>
            <v-btn size="x-small" variant="text" color="primary" @click="flush">
              再試行
            </v-btn>
          </template>
          <template v-else-if="lastSavedAt">
            <v-icon size="14">mdi-check-circle-outline</v-icon>
            {{ lastSavedAt }} に自動保存
          </template>
        </div>

        <v-divider vertical class="mx-1"></v-divider>

        <v-icon size="16" color="grey-darken-1">mdi-magnify</v-icon>
        <v-btn-toggle v-model="zoom" density="compact" variant="outlined" divided mandatory>
          <v-btn v-for="step in zoomSteps" :key="step" :value="step" size="small">
            {{ step }}%
          </v-btn>
        </v-btn-toggle>

        <v-spacer />

        <v-btn
          v-if="detail.permissions.canEdit && detail.plan.planType !== 'overall'"
          size="small"
          variant="outlined"
          color="primary"
          @click="transferDialog = true"
        >
          <v-icon start>mdi-arrow-down-bold-outline</v-icon>
          上位計画から転記
        </v-btn>
        <v-btn size="small" variant="outlined" :loading="exporting" @click="exportExcel">
          <v-icon start>mdi-microsoft-excel</v-icon>
          Excel出力
        </v-btn>
        <v-btn
          v-if="detail.permissions.canDelete"
          size="small"
          variant="outlined"
          color="error"
          :loading="deleting"
          @click="deletePlan"
        >
          <v-icon start>mdi-delete-outline</v-icon>
          削除
        </v-btn>
        <v-btn
          v-if="detail.permissions.canSubmit"
          size="small"
          color="primary"
          variant="flat"
          :disabled="processing"
          @click="openSubmitDialog"
        >
          <v-icon start>mdi-send</v-icon>
          {{ detail.plan.status === "returned" ? "再提出する" : "提出する" }}
        </v-btn>
      </v-card>

      <!-- body -->
      <v-row class="mt-1">
        <v-col cols="12" md="8" lg="9">
          <teaching-plan-grid
            ref="grid"
            :layout="detail.layout"
            :values="detail.values"
            :resolved-references="detail.resolvedReferences"
            :unresolved-comment-counts="detail.unresolvedCommentCounts"
            :transfer-cells="transferCells"
            :editable="detail.permissions.canEdit"
            :selected-item-key="selectedItemKey"
            :zoom="zoom"
            @select="selectCell"
            @change="onCellChange"
            @jump-source="openPlan"
            @transfer-cell="transferSingleCell"
          ></teaching-plan-grid>
        </v-col>
        <v-col cols="12" md="4" lg="3" class="tp-panel-col">
          <teaching-plan-support-panel
            v-if="nurseryId"
            :nursery-id="nurseryId"
            :plan-id="planId"
            :template-id="detail.plan.templateId"
            :layout="detail.layout"
            :selected-item-key="selectedItemKey"
            :comments="comments"
            :editable="detail.permissions.canEdit"
            @insert-text="insertText"
            @jump-plan="openPlan"
            @jump-item="jumpToItem"
            @comments-updated="onCommentsUpdated"
          ></teaching-plan-support-panel>
        </v-col>
      </v-row>

      <!-- 提出モーダル -->
      <v-dialog v-model="submitDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title>提出の確認</v-card-title>
          <v-card-text>
            <template v-if="missingRequiredCells.length > 0">
              <v-alert type="warning" variant="tonal" density="compact">
                必須項目が{{ missingRequiredCells.length }}件未入力のため提出できません。
              </v-alert>
              <ul class="mt-3">
                <li v-for="cell in missingRequiredCells" :key="cell.itemKey">
                  <a href="#" @click.prevent="jumpToMissing(cell.itemKey)">{{ cell.name }}</a>
                </li>
              </ul>
            </template>
            <template v-else>
              <div class="text-body-2">
                {{ planLabel }} を提出します。提出後は承認されるまで編集できません。
              </div>
              <div v-if="detail.workflow.assignedFirstApproverName" class="text-body-2 mt-3">
                承認者: {{ detail.workflow.assignedFirstApproverName }}
                <span v-if="detail.workflow.assignedFinalApproverName">
                  → {{ detail.workflow.assignedFinalApproverName }}
                </span>
              </div>
              <v-alert
                v-if="unresolvedCommentTotal > 0"
                type="info"
                variant="tonal"
                density="compact"
                class="mt-3"
              >
                未解決のコメントが{{ unresolvedCommentTotal }}件あります。
              </v-alert>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" :disabled="submitting" @click="submitDialog = false">
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :loading="submitting"
              :disabled="missingRequiredCells.length > 0"
              @click="submit"
            >
              提出する
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- 差戻しモーダル -->
      <v-dialog v-model="returnDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title>差戻しの確認</v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-3">
              差戻しの理由を入力してください。作成者に通知されます。
            </div>
            <v-textarea
              v-model="returnReason"
              rows="4"
              variant="outlined"
              density="compact"
              color="primary"
              :counter="commentBodyMaxLength"
              placeholder="修正してほしい点を具体的に記入してください"
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" :disabled="returning" @click="returnDialog = false">
              キャンセル
            </v-btn>
            <v-btn
              color="error"
              variant="flat"
              :loading="returning"
              :disabled="!isValidReturnReason"
              @click="returnPlan"
            >
              差戻す
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- 一括転記モーダル -->
      <teaching-plan-transfer-dialog
        v-model="transferDialog"
        :plan-id="planId"
        @transferred="onTransferred"
      ></teaching-plan-transfer-dialog>
    </template>

    <div v-else class="text-center text-grey py-16">
      指導計画が見つかりませんでした。
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import {
  NurseryTeachingPlanComment,
  NurseryTeachingPlanDetailResponse,
} from "chaild-api/lib";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import { TeachingPlanCellTransfer } from "@/components/teaching-plan/TeachingPlanCell.vue";
import TeachingPlanGrid from "@/components/teaching-plan/TeachingPlanGrid.vue";
import TeachingPlanSupportPanel from "@/components/teaching-plan/TeachingPlanSupportPanel.vue";
import TeachingPlanTransferDialog from "@/components/teaching-plan/TeachingPlanTransferDialog.vue";
import LocalDataService from "@/service/LocalDataService";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import { confirmDanger, confirmDiscard, notifyWarning } from "@/utils/TeachingPlanDialog";
import {
  autoSaveDebounceMs,
  commentBodyMaxLength,
  deadlineAlertStatuses,
  statusStyles,
  valueUpsertLimit,
} from "@/constants/teachingPlan";

/**
 * 指導計画の帳票エディタ。
 *
 * 自動保存はセル変更をキューに溜めて一定時間後にまとめてPUTする。
 * 画面遷移・タブ閉じの前には必ずキューを吐き出す。
 */
export default defineComponent({
  name: "TeachingPlanEditor",

  components: { TeachingPlanGrid, TeachingPlanSupportPanel, TeachingPlanTransferDialog },

  async mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    await this.reload();
  },

  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    this.clearTimer();
  },

  async beforeRouteLeave(to: unknown, from: unknown, next: (go?: false) => void) {
    await this.confirmLeave(next);
  },

  // 参照元ジャンプなどで同じコンポーネントのまま planId が変わる場合は
  // beforeRouteLeave が呼ばれないため、こちらで未送信の変更を吐き出す
  async beforeRouteUpdate(to: unknown, from: unknown, next: (go?: false) => void) {
    await this.confirmLeave(next);
  },

  data() {
    return {
      loading: true,
      detail: null as NurseryTeachingPlanDetailResponse | null,
      comments: [] as NurseryTeachingPlanComment[],
      selectedItemKey: null as string | null,
      /** 帳票を切り替えたときに旧帳票の応答を捨てるためのトークン */
      reloadToken: 0,
      zoom: 100,
      /** 表示倍率の段階 (モック準拠) */
      zoomSteps: [100, 90, 80],
      workflowStepIcons: {
        done: "mdi-check-circle",
        current: "mdi-progress-clock",
        todo: "mdi-circle-outline",
      },
      // 自動保存
      pending: {} as { [itemKey: string]: string | null },
      /**
       * サーバの検証に通らない入力 (itemKey -> 理由)。
       * キューに積むと400で失敗し続け、silentErrorなので理由も出ないため分けて持つ。
       */
      invalidCells: {} as { [itemKey: string]: string },
      // pending がどの帳票の変更かを固定する。
      // 別帳票へ遷移した後に残っていた分を送ってしまうと、他の帳票を壊すため
      pendingPlanId: null as string | null,
      saving: false,
      saveFailed: false,
      lastSavedAt: "" as string,
      timerId: null as number | null,
      flushing: null as Promise<void> | null,
      // 転記
      transferDialog: false,
      transferCells: {} as { [itemKey: string]: TeachingPlanCellTransfer },
      // ワークフロー
      submitDialog: false,
      submitting: false,
      returnDialog: false,
      returning: false,
      returnReason: "",
      approving: false,
      deleting: false,
      exporting: false,
      // 定数
      statusStyles,
      commentBodyMaxLength,
    };
  },

  computed: {
    planId(): string {
      return String(this.$route.params.planId || "");
    },
    nurseryId(): number | null {
      return LocalDataService.getNurseryId();
    },
    planLabel(): string {
      return this.detail ? TeachingPlanUtil.listItemLabel(this.detail.plan) : "";
    },
    processing(): boolean {
      return this.submitting || this.returning || this.approving || this.deleting;
    },
    /** 直さない限り保存されない入力の件数 */
    invalidCellCount(): number {
      return Object.keys(this.invalidCells).length;
    },
    hasUnsavedChanges(): boolean {
      return (
        Object.keys(this.pending).length > 0 || this.saveFailed || this.invalidCellCount > 0
      );
    },
    missingRequiredCells(): { itemKey: string; name: string }[] {
      if (!this.detail) {
        return [];
      }
      return TeachingPlanUtil.findMissingRequiredCells(this.detail.layout, this.detail.values);
    },
    unresolvedCommentTotal(): number {
      return this.comments.filter((comment) => !comment.isResolved).length;
    },
    isValidReturnReason(): boolean {
      const trimmed = this.returnReason.trim();
      return trimmed.length > 0 && trimmed.length <= commentBodyMaxLength;
    },
    /**
     * 提出→承認の進行状況。
     * 一段階承認 (最終承認者なし) の承認も finalApprovedAt に入るため、
     * 段数によって並べ方を変える。
     */
    workflowSteps(): {
      label: string;
      name: string | null;
      at: string | null;
      state: "done" | "current" | "todo";
    }[] {
      if (!this.detail || this.detail.plan.status === "draft") {
        return [];
      }
      const plan = this.detail.plan;
      const workflow = this.detail.workflow;
      const steps: { label: string; name: string | null; at: string | null }[] = [
        { label: "提出", name: plan.authorName, at: workflow.submittedAt },
      ];
      if (workflow.assignedFinalApproverName !== null) {
        steps.push({
          label: "一次承認",
          name: workflow.assignedFirstApproverName,
          at: workflow.firstApprovedAt,
        });
        steps.push({
          label: "最終承認",
          name: workflow.assignedFinalApproverName,
          at: workflow.finalApprovedAt,
        });
      } else {
        steps.push({
          label: "承認",
          name: workflow.assignedFirstApproverName,
          at: workflow.finalApprovedAt,
        });
      }
      // 差戻し中は作成者の手元に戻っているため、待ちの担当者は立てない
      const showCurrent = plan.status !== "returned";
      let currentTaken = false;
      return steps.map((step) => {
        if (step.at) {
          return { ...step, state: "done" as const };
        }
        if (showCurrent && !currentTaken) {
          currentTaken = true;
          return { ...step, state: "current" as const };
        }
        return { ...step, state: "todo" as const };
      });
    },
    /** 未提出のまま期限日を過ぎているか (X-5 の超過強調) */
    isDeadlineOverdue(): boolean {
      if (!this.detail || !this.detail.plan.deadlineDate) {
        return false;
      }
      if (!deadlineAlertStatuses.includes(this.detail.plan.status)) {
        return false;
      }
      return dayjs(this.detail.plan.deadlineDate).isBefore(dayjs().startOf("day"));
    },
    approverSubText(): string {
      if (!this.detail) {
        return "";
      }
      if (this.detail.plan.status === "firstApproved") {
        return "一次承認が済んでいます。最終承認をお願いします。";
      }
      return "内容を確認して承認するか、修正が必要なら差戻してください。";
    },
    lockedReason(): string {
      if (!this.detail) {
        return "";
      }
      switch (this.detail.plan.status) {
        case "approved":
          return "承認済みのため編集できません。";
        case "submitted":
        case "resubmitted":
        case "reviewing":
        case "firstApproved":
          return "提出済みのため編集できません。修正が必要な場合は承認者に差戻しを依頼してください。";
        default:
          return "この指導計画を編集する権限がありません。";
      }
    },
  },

  watch: {
    /** 参照元ジャンプなどで同じコンポーネントのまま planId が変わるケース */
    planId() {
      this.reload();
    },
  },

  methods: {
    formatDate(value: string | null): string {
      return TeachingPlanUtil.formatDate(value);
    },
    formatDateTime(value: string | null): string {
      return TeachingPlanUtil.formatDateTime(value);
    },
    backToList() {
      this.$router.push({ path: "/teaching-plan/list" });
    },
    openPlan(planId: string) {
      this.$router.push({ path: `/teaching-plan/${planId}` });
    },
    selectCell(itemKey: string | null) {
      this.selectedItemKey = itemKey;
    },
    jumpToItem(itemKey: string) {
      this.selectedItemKey = itemKey;
      const grid = this.$refs.grid as InstanceType<typeof TeachingPlanGrid> | undefined;
      if (grid) {
        grid.scrollToCell(itemKey);
      }
    },
    jumpToMissing(itemKey: string) {
      this.submitDialog = false;
      this.jumpToItem(itemKey);
    },
    insertText(text: string) {
      if (!this.selectedItemKey) {
        return;
      }
      const grid = this.$refs.grid as InstanceType<typeof TeachingPlanGrid> | undefined;
      if (grid) {
        grid.insertTextToCell(this.selectedItemKey, text);
      }
    },
    onCommentsUpdated(comments: NurseryTeachingPlanComment[]) {
      this.comments = comments;
      // 承認者のコメント投稿でステータスが確認中へ自動遷移するため詳細も引き直す
      this.loadDetail(this.planId, this.reloadToken);
    },

    /**
     * 読み込み
     *
     * 参照元ジャンプで同じコンポーネントのまま planId が変わるため、
     * 旧帳票の応答が後着して新しい画面に混ざらないよう世代トークンで照合する。
     */
    async reload() {
      this.reloadToken += 1;
      const token = this.reloadToken;
      const planId = this.planId;
      this.loading = true;
      // 読み込みに失敗したときに旧帳票が新しいURL上に残らないよう、先に捨てる
      // (残すと旧帳票の内容を編集でき、その入力は新帳票へ送られてしまう)
      this.detail = null;
      this.comments = [];
      this.transferCells = {};
      this.selectedItemKey = null;
      this.invalidCells = {};
      // 開いたままのモーダルは新しい帳票の上で再表示されてしまうので閉じる
      // (提出・差戻しは新しい帳票に対して実行できてしまうため)
      this.submitDialog = false;
      this.returnDialog = false;
      this.transferDialog = false;
      this.returnReason = "";
      // 処理中フラグは帳票ごとの状態。前の帳票の応答がこれを解除して
      // 新しい帳票のボタンを二度押しできるようにしないよう、ここで初期化する
      this.submitting = false;
      this.approving = false;
      this.returning = false;
      this.deleting = false;
      this.exporting = false;
      await this.loadDetail(planId, token);
      if (token !== this.reloadToken) {
        return;
      }
      this.loading = false;
      await Promise.all([
        this.loadComments(planId, token),
        this.loadTransferTargets(planId, token),
      ]);
    },
    async loadDetail(planId: string, token: number) {
      if (!planId) {
        return;
      }
      const response = await ApiTeachingPlan.findPlan({ planId }).catch(() => null);
      if (token !== this.reloadToken || planId !== this.planId) {
        return;
      }
      if (response) {
        this.detail = response;
      }
    },
    async loadComments(planId: string, token: number) {
      if (!planId) {
        return;
      }
      const response = await ApiTeachingPlan.listComments({ planId }).catch(() => null);
      if (token !== this.reloadToken || planId !== this.planId) {
        return;
      }
      this.comments = response ? response.data : [];
    },
    /** 未入力のcarry転記先セルに引き込み導線を出すため候補を先読みする */
    async loadTransferTargets(planId: string, token: number) {
      if (!this.detail || this.detail.plan.planType === "overall") {
        this.transferCells = {};
        return;
      }
      const response = await ApiTeachingPlan.listTransferCandidates({
        planId,
      }).catch(() => null);
      if (token !== this.reloadToken || planId !== this.planId) {
        return;
      }
      if (!response) {
        this.transferCells = {};
        return;
      }
      // 転記先セルであること自体は入力済みでも表示し続ける (NANDO-20 転記セルの表示)。
      // 引き込みの導線は「未入力かつ転記元が解決済み」のときだけ出す
      const cells: { [itemKey: string]: TeachingPlanCellTransfer } = {};
      for (const candidate of response.data) {
        cells[candidate.itemKey] = {
          sourcePlanId: candidate.sourcePlanId,
          sourcePlanLabel: candidate.sourcePlanLabel,
          resolution: candidate.resolution,
          canPull:
            candidate.resolution === "resolved" &&
            candidate.candidateValue !== null &&
            candidate.candidateValue !== "" &&
            (candidate.currentValue === null || candidate.currentValue === ""),
        };
      }
      this.transferCells = cells;
    },

    /**
     * 自動保存
     */
    onCellChange(payload: { itemKey: string; value: string; error?: string }) {
      if (!this.detail || !this.detail.permissions.canEdit) {
        return;
      }
      // サーバが400で弾く値はキューに積まない。積むと自動保存が理由の出ないまま
      // 失敗し続け、再試行しても直らず離脱も止められて詰む
      if (payload.error) {
        this.invalidCells[payload.itemKey] = payload.error;
        // 同じセルの古い変更がキューに残っていると、画面は新しい値なのに
        // 古い値が保存される。「保存できません」と出しながら別の値が入るのは
        // 表示と実態の食い違いなので、キューから落とす
        delete this.pending[payload.itemKey];
        if (Object.keys(this.pending).length === 0) {
          this.pendingPlanId = null;
          this.clearTimer();
        }
        return;
      }
      delete this.invalidCells[payload.itemKey];
      const current = this.detail.values[payload.itemKey];
      const currentValue = current ? current.value : null;
      const nextValue = payload.value === "" ? null : payload.value;
      if (currentValue === nextValue && !(payload.itemKey in this.pending)) {
        return;
      }
      this.pendingPlanId = this.planId;
      this.pending[payload.itemKey] = nextValue;
      this.scheduleFlush();
    },
    scheduleFlush() {
      this.clearTimer();
      this.timerId = window.setTimeout(() => {
        this.flush();
      }, autoSaveDebounceMs);
    },
    clearTimer() {
      if (this.timerId !== null) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }
    },
    /**
     * 未送信の変更をすべて送る。
     * 実行中の保存があれば、それを待つだけにして二重送信を避ける
     * (実行中のflushがキューを空になるまで回すため、待てば取りこぼさない)。
     */
    async flush(): Promise<void> {
      this.clearTimer();
      if (this.flushing) {
        await this.flushing;
        return;
      }
      if (Object.keys(this.pending).length === 0 || !this.detail) {
        return;
      }
      this.flushing = this.drainPending();
      try {
        await this.flushing;
      } finally {
        this.flushing = null;
      }
    },
    /** キューが空になるまで送り続ける (APIは1リクエスト50件まで) */
    async drainPending(): Promise<void> {
      // 送信先は変更を積んだ時点の帳票に固定する (途中で別帳票へ移っても混ざらない)
      const planId = this.pendingPlanId;
      if (!planId) {
        return;
      }
      this.saving = true;
      this.saveFailed = false;
      while (Object.keys(this.pending).length > 0 && this.pendingPlanId === planId) {
        const chunk = Object.entries(this.pending).slice(0, valueUpsertLimit);
        for (const [itemKey] of chunk) {
          delete this.pending[itemKey];
        }
        const response = await ApiTeachingPlan.upsertValues({
          planId,
          values: chunk.map(([itemKey, value]) => ({ itemKey, value })),
        }).catch(() => null);
        if (!response) {
          this.saveFailed = true;
          // 失敗した分は書き戻して次回に再送する (未送信の変更が消えないようにする)。
          // 送信中に同じセルが編集されていたら、新しい値のほうを優先する。
          // 送信中に不正値の入力でキューが空になると pendingPlanId が null に戻されるため、
          // 書き戻しと同時に復元しないと「再試行」が何もしなくなる (別帳票のキューには混ぜない)
          if (this.pendingPlanId === null || this.pendingPlanId === planId) {
            this.pendingPlanId = planId;
            for (const [itemKey, value] of chunk) {
              if (!(itemKey in this.pending)) {
                this.pending[itemKey] = value;
              }
            }
          }
          break;
        }
        // 応答が返る間に別帳票へ移っていたら画面には反映しない
        if (this.detail && this.planId === planId) {
          for (const saved of response.values) {
            this.detail.values[saved.itemKey] = {
              value: saved.value,
              updatedAt: saved.updatedAt,
              updatedByName: saved.updatedByName,
            };
          }
        }
        this.lastSavedAt = dayjs().format("HH:mm");
      }
      this.saving = false;
    },
    onBeforeUnload(event: BeforeUnloadEvent) {
      if (!this.hasUnsavedChanges && !this.saving) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    },
    /**
     * 画面遷移前に未送信の変更を吐き出す。
     * 保存しきれなかった場合は破棄の確認を取り、承諾されたらキューを捨てる
     * (残したままにすると遷移先の帳票へ書き込まれてしまう)。
     */
    async confirmLeave(next: (go?: false) => void) {
      await this.flush();
      if (!this.hasUnsavedChanges) {
        next();
        return;
      }
      // 通信で保存できていないのか、値が不正で保存されないのかで直し方が違う
      const title =
        this.invalidCellCount > 0
          ? `保存できない入力が${this.invalidCellCount}件あります`
          : "保存できていない変更があります";
      const text =
        this.invalidCellCount > 0
          ? "赤く囲まれたセルは、直すまで保存されません。破棄して移動しますか？"
          : "変更を破棄してこのまま移動しますか？";
      if (!(await confirmDiscard(title, text))) {
        next(false);
        return;
      }
      this.pending = {};
      this.pendingPlanId = null;
      this.saveFailed = false;
      this.invalidCells = {};
      next();
    },

    /**
     * 転記
     */
    /**
     * 一括転記のあとは値と引き込み導線だけ引き直す
     * (reload() は詳細を捨てて読み込み直すため、画面がちらつく)
     */
    async onTransferred() {
      const planId = this.planId;
      const token = this.reloadToken;
      await this.loadDetail(planId, token);
      await this.loadTransferTargets(planId, token);
    },
    async transferSingleCell(itemKey: string) {
      const planId = this.planId;
      const token = this.reloadToken;
      const response = await ApiTeachingPlan.transfer({
        planId,
        itemKeys: [itemKey],
        overwrite: false,
      }).catch(() => null);
      if (response && this.isCurrent(planId, token)) {
        await this.loadDetail(planId, token);
        await this.loadTransferTargets(planId, token);
      }
    },

    /**
     * ワークフロー
     *
     * 応答が返る間に別帳票へ移りうるため、いずれも操作対象の planId と世代を固定し、
     * 一致するときだけ画面へ反映する (別帳票の画面に前の帳票の状態と操作ボタンが
     * 出てしまうと、誤って承認・差戻し・削除できてしまうため)。
     */
    isCurrent(planId: string, token: number): boolean {
      return this.planId === planId && this.reloadToken === token;
    },
    async openSubmitDialog() {
      // 未送信の変更を確定させてから必須チェックする
      await this.flush();
      // 値が不正で保存されていないのに「通信状況を確認」と言うと直し方を誤らせる
      if (this.invalidCellCount > 0) {
        notifyWarning(
          `保存できない入力が${this.invalidCellCount}件あります`,
          "赤く囲まれたセルを直してから提出してください。"
        );
        return;
      }
      if (this.hasUnsavedChanges) {
        notifyWarning(
          "保存されていない変更があります",
          "通信状況を確認してからもう一度お試しください。"
        );
        return;
      }
      this.submitDialog = true;
    },
    async submit() {
      const planId = this.planId;
      const token = this.reloadToken;
      this.submitting = true;
      const response = await ApiTeachingPlan.submitPlan({ planId }).catch(() => null);
      // 世代が変わっていたらフラグは触らない (reload側で初期化済み)。
      // 先に解除すると、別帳票で進行中の操作のボタンが再度押せてしまう
      if (!this.isCurrent(planId, token)) {
        return;
      }
      this.submitting = false;
      if (response) {
        this.detail = response;
        this.submitDialog = false;
        await this.loadTransferTargets(planId, token);
      }
    },
    async approve() {
      const planId = this.planId;
      const token = this.reloadToken;
      this.approving = true;
      const response = await ApiTeachingPlan.approvePlan({ planId }).catch(() => null);
      if (!this.isCurrent(planId, token)) {
        return;
      }
      this.approving = false;
      if (response) {
        this.detail = response;
      }
    },
    async returnPlan() {
      if (!this.isValidReturnReason) {
        return;
      }
      const planId = this.planId;
      const token = this.reloadToken;
      this.returning = true;
      const response = await ApiTeachingPlan.returnPlan({
        planId,
        reason: this.returnReason.trim(),
      }).catch(() => null);
      if (!this.isCurrent(planId, token)) {
        return;
      }
      this.returning = false;
      if (response) {
        this.detail = response;
        this.returnDialog = false;
        this.returnReason = "";
      }
    },
    async deletePlan() {
      if (
        !(await confirmDanger(
          `${this.planLabel} を削除しますか？`,
          "入力した内容・コメントもすべて削除されます。この操作は取り消せません。"
        ))
      ) {
        return;
      }
      const planId = this.planId;
      const token = this.reloadToken;
      this.deleting = true;
      const response = await ApiTeachingPlan.deletePlan({ planId }).catch(() => null);
      if (!this.isCurrent(planId, token)) {
        return;
      }
      this.deleting = false;
      if (response) {
        this.backToList();
      }
    },
    async exportExcel() {
      // 保存されていない値はExcelに反映されない。画面と食い違う帳票を黙って作らない
      await this.flush();
      if (this.invalidCellCount > 0) {
        notifyWarning(
          `保存できない入力が${this.invalidCellCount}件あります`,
          "赤く囲まれたセルを直してから出力してください。"
        );
        return;
      }
      if (this.hasUnsavedChanges) {
        notifyWarning(
          "保存されていない変更があります",
          "通信状況を確認してからもう一度お試しください。"
        );
        return;
      }
      const planId = this.planId;
      this.exporting = true;
      const response = await ApiTeachingPlan.exportExcel({ planId }).catch(() => null);
      // 応答が返る間に別帳票へ移っていたら、新しい帳票の出力状態を触らない (reloadで初期化済み)
      if (this.planId !== planId) {
        return;
      }
      this.exporting = false;
      if (response && response.url) {
        window.open(response.url, "_blank");
      }
    },
  },
});
</script>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.tp-pre {
  white-space: pre-wrap;
  word-break: break-word;
}
.deadline-overdue {
  display: inline-block;
  padding: 0 6px;
  border-radius: 8px;
  background-color: #c62828;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}
/*
 * 入力支援パネルはグリッドの高さに引き伸ばされると内側スクロールになり、
 * 「コメントする」などの操作が枠外に隠れる。画面に追従させて、
 * 高さは中身なり (最大でも画面いっぱい) にする。
 */
.tp-panel-col {
  align-self: flex-start;
  position: sticky;
  /* アプリバー (v-app-bar height=40px) の下。0にすると頭が隠れる */
  top: 48px;
}
.tp-workflow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
}
.tp-workflow__step {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #90a4ae;
}
.tp-workflow__step:not(:last-child)::after {
  content: "›";
  margin: 0 10px;
  color: #cfd8dc;
  font-size: 16px;
}
.tp-workflow__step--done {
  color: #2e7d32;
}
.tp-workflow__step--current {
  color: #ef6c00;
  font-weight: 700;
}
.tp-workflow__label {
  font-weight: 700;
}
.tp-workflow__name {
  color: #455a64;
}
.tp-workflow__at {
  color: #90a4ae;
}
</style>
